import type {
  ActionsConfig,
  DeriveActionMap,
  ModuleDefinition,
  VersionString,
} from "@bestowinc/tinkertoy-core";
import { useMemo } from "@bestowinc/tinkertoy-shell-deps/react";

import { useTinkertoy } from "../router/use-tinkertoy";
import { useShellRuntime } from "../shell-runtime";
import { useViewIdentity } from "../view-identity";
import { createClientActionMap } from "./create-client-action-map";

export type UseClientActionMapOptions = {
  /**
   * Exact version of the module the renderer mounted (drawn from
   * `loadedView.manifest.modules[moduleId].version`). Forwarded to the
   * server on every action call for manifest validation.
   *
   * Cannot be drawn from {@link useViewIdentity} because each module on
   * the same view has its own version.
   */
  moduleVersion: VersionString;
};

/**
 * Builds a module `actions` map tied to the current shell: POSTs go to the
 * server action endpoint, and **successful** calls trigger
 * {@link useTinkertoy} `revalidate()` so the route loader reruns without
 * changing the URL.
 *
 * Per-action revalidation is controlled by the `shouldRevalidate` field on
 * each action's config (defaults to `true`).
 *
 * View-level identity (`viewId` + `viewVersion`) is read from
 * {@link useViewIdentity}; the action endpoint's base URL is read from
 * {@link useShellRuntime}. Both contexts are wired by the Tinkertoy shell
 * (`<ViewRenderer>` provides view identity; `InternalTinkertoyProvider`
 * provides shell runtime).
 *
 * The map is rebuilt when the module definition, module version, the
 * containing view's identity, or the shell runtime config changes —
 * matching the design contract that action handlers are NOT shared
 * across views.
 *
 * The module definition should be stable (module scope or `useMemo`) to
 * avoid recreating action functions every render.
 */
export function useClientActionMap<TActions extends ActionsConfig>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- FC<any> matches any module component variant
  definition: Pick<ModuleDefinition<any, TActions>, "id" | "actions">,
  { moduleVersion }: UseClientActionMapOptions,
): DeriveActionMap<TActions> {
  const { revalidate } = useTinkertoy();
  const { viewId, viewVersion } = useViewIdentity();
  const { apiBaseUrl } = useShellRuntime();

  return useMemo(
    () =>
      createClientActionMap(definition, {
        apiBaseUrl,
        moduleVersion,
        viewId,
        viewVersion,
        revalidate,
      }),
    [definition, moduleVersion, viewId, viewVersion, apiBaseUrl, revalidate],
  );
}
